/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import SearchIcon from '@atlaskit/icon-lab/core/ai-search';
import ChatIcon from '@atlaskit/icon/core/rovo-chat';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { PageMode } from '../../constants/page-mode';

import { i18n } from './messages';

export type ModeToggleProps = {
	pageMode: PageMode;
	onChange: (next: PageMode) => void;
};

const styles = cssMap({
	track: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
	button: {
		height: '24px',
		display: 'flex',
		gap: token('space.050'),
		alignItems: 'center',
		paddingTop: token('space.0'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.075'),
		borderRadius: token('radius.small'),
		color: token('color.text'),
		backgroundColor: token('color.background.neutral.subtle'),
		cursor: 'pointer',
		whiteSpace: 'nowrap',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	selectedButton: {
		color: token('color.text.selected'),
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
	buttonIcon: {
		width: '14px',
		height: '14px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

interface ModeButtonProps {
	mode: PageMode;
	isSelected: boolean;
	icon: ReactNode;
	label: string;
	onSelect: (mode: PageMode) => void;
}

/**
 * A single option in the segmented control. Rendered as a real toggle button
 * (`Pressable` + `aria-pressed`) so screen readers announce e.g. "Chat, toggle
 * button, pressed". Both buttons stay in the natural Tab order; Enter/Space
 * activate via the underlying `<button>`.
 */
const ModeButton = ({ mode, isSelected, icon, label, onSelect }: ModeButtonProps) => (
	<Pressable
		xcss={cx(styles.button, isSelected && styles.selectedButton)}
		testId={`search-page-mode-toggle-${mode}`}
		aria-pressed={isSelected}
		onClick={() => onSelect(mode)}
	>
		<Box xcss={styles.buttonIcon}>{icon}</Box>
		<Text color="inherit">{label}</Text>
	</Pressable>
);

export const ModeToggle = ({ pageMode, onChange }: ModeToggleProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box role="group" aria-label={formatMessage(i18n.groupLabel)} testId="search-page-mode-toggle">
			<Box xcss={styles.track}>
				<ModeButton
					mode="chat"
					isSelected={pageMode === 'chat'}
					icon={<ChatIcon label="" />}
					label={formatMessage(i18n.chat)}
					onSelect={onChange}
				/>
				<ModeButton
					mode="search"
					isSelected={pageMode === 'search'}
					icon={<SearchIcon label="" />}
					label={formatMessage(i18n.search)}
					onSelect={onChange}
				/>
			</Box>
		</Box>
	);
};
